import { Lock, ShieldCheck, ShieldAlert, ShieldX } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import type { SpamReport } from '@/types/email';

interface SecurityBadgesProps {
  hasPgp: boolean;
  hasSmime: boolean;
  spamReport?: SpamReport | null;
  headers?: Record<string, unknown> | null;
}

// Rspamd symbol mappings
const SPF_SYMBOLS = {
  pass: ['R_SPF_ALLOW'],
  warn: ['R_SPF_SOFTFAIL', 'R_SPF_NEUTRAL', 'R_SPF_DNSFAIL'],
  fail: ['R_SPF_FAIL', 'R_SPF_PERMFAIL'],
};
const DKIM_SYMBOLS = {
  pass: ['R_DKIM_ALLOW'],
  warn: ['R_DKIM_TEMPFAIL'],
  fail: ['R_DKIM_REJECT'],
};
const DMARC_SYMBOLS = {
  pass: ['DMARC_POLICY_ALLOW'],
  warn: ['DMARC_POLICY_QUARANTINE', 'DMARC_POLICY_SOFTFAIL'],
  fail: ['DMARC_POLICY_REJECT'],
};

type AuthResult = 'pass' | 'warn' | 'fail' | 'none';

function getAuthResult(
  symbolMap: { pass: string[]; warn: string[]; fail: string[] },
  symbols: Record<string, unknown>,
): AuthResult {
  const keys = Object.keys(symbols);
  if (symbolMap.fail.some((s) => keys.includes(s))) return 'fail';
  if (symbolMap.warn.some((s) => keys.includes(s))) return 'warn';
  if (symbolMap.pass.some((s) => keys.includes(s))) return 'pass';
  return 'none';
}

const resultLabel: Record<AuthResult, string> = {
  pass: 'Pass',
  warn: 'Warning',
  fail: 'Fail',
  none: 'Not checked',
};

const dotColor: Record<AuthResult, string> = {
  pass: 'bg-success',
  warn: 'bg-warning',
  fail: 'bg-danger',
  none: 'bg-muted-foreground/40',
};

function AuthBadge({ spamReport }: { spamReport: SpamReport }) {
  const symbols = spamReport.symbols ?? {};

  const spf = getAuthResult(SPF_SYMBOLS, symbols);
  const dkim = getAuthResult(DKIM_SYMBOLS, symbols);
  const dmarc = getAuthResult(DMARC_SYMBOLS, symbols);

  // Don't render if no auth results detected
  if (spf === 'none' && dkim === 'none' && dmarc === 'none') {
    return null;
  }

  // Determine overall status
  const results = [spf, dkim, dmarc];
  const hasFail = results.includes('fail');
  const hasWarn = results.includes('warn');

  let Icon = ShieldCheck;
  let badgeClass = 'bg-success-surface text-success-ink border-success-line';

  if (hasFail) {
    Icon = ShieldX;
    badgeClass = 'bg-danger-surface text-danger-ink border-danger-line';
  } else if (hasWarn) {
    Icon = ShieldAlert;
    badgeClass = 'bg-warning-surface text-warning-ink border-warning-line';
  }

  const rows: { label: string; result: AuthResult }[] = [
    { label: 'SPF', result: spf },
    { label: 'DKIM', result: dkim },
    { label: 'DMARC', result: dmarc },
  ];

  return (
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>
        <Badge className={`rounded-full border px-1.5 py-0.5 ${badgeClass}`}>
          <Icon className="h-3 w-3" />
        </Badge>
      </TooltipTrigger>
      <TooltipContent className="space-y-2 text-xs">
        <div className="font-semibold text-base">Email authentication</div>
        <div className="space-y-1.5 border-t pt-2">
          {rows.map((row) => (
            <div key={row.label} className="flex items-center justify-between gap-4">
              <span>{row.label}</span>
              <span className="flex items-center gap-1">
                <span className={`h-1.5 w-1.5 rounded-full ${dotColor[row.result]}`} />
                {resultLabel[row.result]}
              </span>
            </div>
          ))}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

export function SecurityBadges({ hasPgp, hasSmime, spamReport }: SecurityBadgesProps) {
  const hasAuth = spamReport?.symbols && Object.keys(spamReport.symbols).length > 0;

  if (!hasPgp && !hasSmime && !hasAuth) {
    return null;
  }

  return (
    <div className="flex items-center gap-1">
      {hasPgp && (
        <Badge variant="outline" className="gap-1">
          <Lock className="h-3 w-3" />
          PGP
        </Badge>
      )}
      {hasSmime && (
        <Badge variant="outline" className="gap-1">
          <ShieldCheck className="h-3 w-3" />
          S/MIME
        </Badge>
      )}
      {spamReport && <AuthBadge spamReport={spamReport} />}
    </div>
  );
}
